import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getEmailRedirectUrl, detectEnvironment, logEnvironmentInfo } from "@/utils/environmentRedirects";
import { useToast } from "@/hooks/use-toast";

/**
 * Custom hook for SMS invitation signup that bypasses email verification
 */
export const useEnvironmentAwareSMSAuth = () => {
  const { toast } = useToast();
  
  // Log current environment on hook initialization
  const currentEnv = logEnvironmentInfo();

  const signUpSMSInviteWithoutVerification = useCallback(async (
    email: string,
    password: string,
    options: {
      displayName?: string;
      role?: string;
      phoneNumber?: string;
    } = {}
  ) => {
    try {
      console.log(`[SMSAuth] Starting SMS invitation signup without email verification:`, {
        environment: currentEnv.type,
        email: email.trim(),
        phoneNumber: options.phoneNumber,
        role: options.role
      });

      // For SMS invitations, we don't require email confirmation
      // We'll use the auth admin functions to create a verified user directly
      const signUpOptions = {
        email: email.trim(),
        password: password.trim(),
        options: {
          data: {
            display_name: options.displayName || email.split('@')[0],
            role: options.role || 'vendor',
            sms_invitation: true,
            phone_number: options.phoneNumber
          },
          // We still provide redirect URL but won't require email verification
          emailRedirectTo: getEmailRedirectUrl('signup')
        }
      };

      const result = await supabase.auth.signUp(signUpOptions);
      
      if (result.error) {
        throw result.error;
      }

      // If we successfully created the user via SMS invitation,
      // we should mark them as email verified immediately
      if (result.data?.user && !result.data.user.email_confirmed_at) {
        console.log('[SMSAuth] SMS user created, will be verified automatically by backend trigger');
      }
      
      return result;
    } catch (error) {
      console.error('[SMSAuth] SMS signup error:', error);
      throw error;
    }
  }, [currentEnv]);

  const getCurrentEnvironmentInfo = useCallback(() => {
    return currentEnv;
  }, [currentEnv]);

  return {
    signUpSMSInviteWithoutVerification,
    getCurrentEnvironmentInfo,
    currentEnvironment: currentEnv
  };
};